import { zodResolver } from "@hookform/resolvers/zod";
import { ActionIcon, Affix, Anchor, Badge, Box, Button, Card, CheckIcon, Group, Loader, Stack, Text, Title, Tooltip } from "@mantine/core";
import { useClipboard } from "@mantine/hooks";
import { modals } from "@mantine/modals";
import { PlusIcon, TrashIcon, WarningCircleIcon } from "@phosphor-icons/react";
import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useRef } from "react";
import { SubmitHandler, useFieldArray, useForm } from "react-hook-form";
import z from "zod";

import { Input } from "@/components/_Input";
import { DashboardComponents } from "@/features/dashboard/_components/dashboard";
import { useSelectedCompany } from "@/features/dashboard/configuracoes/_context/SelectedCompanyContext";
import { useIsMobile } from "@/hooks";
import { useGetIpQuery, useGetSettingsByCompanyQuery, useResetQueueMutation, useUpdateSettingsMutation } from "@/store";
import { cn, toValidId } from "@/utils";

export const Route = createFileRoute("/_private/dashboard/_settings/configuracoes/")({
  component: RouteComponent
});

const timeRegex = /^\d{2}:\d{2}$/; // Assuming HH:MM format

type Schema = z.infer<typeof schema>;
const schema = z.object({
  id: z.number().nullable(),
  googleMaps: z.string(),
  delay: z.array(z.any()),
  company: z.number().nullable(),
  enableQueue: z.boolean(),
  enableQueueWithoutScheduling: z.boolean(),
  checkinIp: z.array(z.object({ value: z.ipv4({ error: "Ip inválido!" }) })).min(1, "Adicione pelo menos um Ip!"),
  enableCheckin: z.boolean(),
  startTime: z.string().regex(timeRegex),
  endTime: z.string().regex(timeRegex),
  maxDailyServices: z.number(),
  serviceTime: z.string().regex(timeRegex),
  editHoursLimit: z.number().min(0).max(24),
  intervalStartTime: z.string().regex(timeRegex),
  intervalEndTime: z.string().regex(timeRegex),
  weekDays: z.object({
    sun: z.boolean(),
    mon: z.boolean(),
    tue: z.boolean(),
    wed: z.boolean(),
    thu: z.boolean(),
    fri: z.boolean(),
    sat: z.boolean()
  }),
  delayTolerance: z.string().regex(timeRegex),
  enableDelayTolerance: z.boolean(),
  recaptchaSecret: z.string(),
  enableRecaptcha: z.boolean(),
  enableMissingValidation: z.boolean(),
  missingLimit: z.number().min(0),
  missingLimitAfterBlock: z.number().min(0),
  missingValidationPeriod: z.number().min(0),
  schedulingDayLimit: z.number().min(0),
  hourEntryLimit: z.number().min(0),
  hour_entry_limit_attended: z.number().min(0).nullable(),
  hour_entry_limit_missing: z.number().min(0).nullable()
});

function RouteComponent() {
  const { companyId } = useSelectedCompany();
  const clipboard = useClipboard({ timeout: 1000 });
  const isMobile = useIsMobile();

  const validCompanyId = toValidId(companyId);
  const { data, isFetching, isSuccess, refetch } = useGetSettingsByCompanyQuery({ id: validCompanyId! }, { skip: !validCompanyId });
  const { data: ipData, isFetching: isFetchingIp, isSuccess: isSuccessIp, refetch: refetchIp } = useGetIpQuery();
  const [resetQueue, { isLoading }] = useResetQueueMutation();
  const [editSettings] = useUpdateSettingsMutation();

  const {
    formState: { isSubmitting, isDirty, disabled },
    handleSubmit,
    control,
    reset
  } = useForm({
    mode: "all",
    disabled: isFetching || !isSuccess,
    resolver: zodResolver(schema),
    defaultValues: {
      id: null,
      googleMaps: "",
      delay: [],
      company: null,
      enableQueue: false,
      enableQueueWithoutScheduling: false,
      checkinIp: [{ value: "" }, { value: "" }],
      enableCheckin: false,
      startTime: "",
      endTime: "",
      maxDailyServices: 0,
      serviceTime: "",
      editHoursLimit: 0,
      intervalStartTime: "",
      intervalEndTime: "",
      weekDays: {
        sun: false,
        mon: false,
        tue: false,
        wed: false,
        thu: false,
        fri: false,
        sat: false
      },
      delayTolerance: "",
      enableDelayTolerance: false,
      recaptchaSecret: "",
      enableRecaptcha: false,
      enableMissingValidation: false,
      missingLimit: 0,
      missingLimitAfterBlock: 0,
      missingValidationPeriod: 0,
      schedulingDayLimit: 0,
      hourEntryLimit: 0
    }
  });
  const { fields, remove, append } = useFieldArray({
    control,
    name: "checkinIp"
  });

  const onSubmit: SubmitHandler<Schema> = async (values) => {
    const { id, googleMaps, delay, company: _company, weekDays, recaptchaSecret, checkinIp } = values;

    const ips = checkinIp.map((ip) => ip.value).join(",");

    // Transformar os valores para o formato do antigo projeto
    const transformedValues = {
      id: id as number,
      googleMaps: googleMaps || "",
      delay: delay || [],
      company: Number(companyId),
      weekDays: weekDays,
      checkinIp: ips,
      recaptchaSecret: recaptchaSecret || null,
      enableCheckin: values.enableCheckin,
      programmedTotal: 0,
      // programmedTotal: values.maxDailyServices,
      queue_edit_hours_limit: values.editHoursLimit,
      timeAttendence: values.serviceTime,
      start: values.startTime,
      end: values.endTime,
      startInterval: values.intervalStartTime,
      endInterval: values.intervalEndTime,
      scheduling_day_limit: values.schedulingDayLimit,
      hour_entry_Limit: values.hourEntryLimit,
      enableRecaptcha: values.enableRecaptcha,
      enableTolerance: values.enableDelayTolerance,
      tolerance: values.delayTolerance,
      enable_missing_validation: values.enableMissingValidation,
      missing_limit: values.missingLimit,
      missing_limit_after_block: values.missingLimitAfterBlock,
      missing_validation_period: values.missingValidationPeriod,
      enableQueueSave: values.enableQueue,
      enableQueueWithoutScheduling: values.enableQueueWithoutScheduling,
      hour_entry_limit_attended: values.hour_entry_limit_attended,
      hour_entry_limit_missing: values.hour_entry_limit_missing
    };

    await editSettings(transformedValues);
  };

  const handleRefetch = () => {
    refetch();
    refetchIp();
  };

  const handleResetQueue = () => {
    modals.openConfirmModal({
      title: (
        <Text fw="bold" size="2xl">
          Deseja resetar a fila?
        </Text>
      ),
      children: (
        <Text ta="center">
          Ao fazer essa ação, todos os veículos serão realocados para o inicio do período vigente e distribuídos nos dias seguintes de acordo com as regras determinadas, podendo
          haver adiantamentos abruptos de atendimento. Deseja continuar?
        </Text>
      ),
      groupProps: {
        className: cn("grid! grid-cols-1 gap-2! md:grid-cols-2")
      },
      confirmProps: { color: "red", loading: isLoading },
      cancelProps: { disabled: isLoading },
      labels: { confirm: "Sim", cancel: "Não" },
      onConfirm: async () => await resetQueue({ company: { id: Number(companyId) } })
    });
  };

  useEffect(() => {
    if (!isFetching && isSuccess) {
      const { weekDays } = data;

      reset((values) => ({
        ...values,
        id: data.id,
        enableCheckin: data.enableCheckin,
        enableQueue: data.enableQueueSave,
        enableQueueWithoutScheduling: data.enableQueueWithoutScheduling,
        checkinIp: data.checkinIp.split(",").map((ip) => ({
          value: ip
        })),
        weekDays: weekDays,
        startTime: data.start,
        endTime: data.end,
        // maxDailyServices: data.programmedTotal,
        delay: data.delay,
        maxDailyServices: 0,
        serviceTime: data.timeAttendence,
        editHoursLimit: data.queue_edit_hours_limit,
        intervalStartTime: data.startInterval,
        intervalEndTime: data.endInterval,
        enableDelayTolerance: data.enableTolerance,
        delayTolerance: data.tolerance,
        recaptchaSecret: data.recaptchaSecret || "",
        enableRecaptcha: data.enableRecaptcha,
        enableMissingValidation: data.enable_missing_validation,
        missingLimit: data.missing_limit,
        missingLimitAfterBlock: data.missing_limit_after_block,
        missingValidationPeriod: data.missing_validation_period,
        schedulingDayLimit: data.scheduling_day_limit,
        hourEntryLimit: data.hour_entry_Limit,
        hour_entry_limit_attended: data.hour_entry_limit_attended || 0,
        hour_entry_limit_missing: data.hour_entry_limit_missing || 0
      }));
    }

    /* if (!isSuccess) {
      alert("Erro");
      reset(undefined);
    } */
  }, [isFetching, data, isSuccess]);

  const wrapperRef = useRef<HTMLDivElement>(null);
  const wrapperRect = wrapperRef.current?.getBoundingClientRect();
  const Wrapper = isMobile ? Affix : Group;
  const wrapperStyles = cn("dark:bg-black-700/30 not-dark:bg-white-700/30 z-100 rounded-b-md p-1.5 backdrop-blur-md");
  const WrapperProps = new Map<boolean, object>([
    [
      true,
      {
        left: 0,
        right: 0,
        top: 60,
        withinPortal: false,
        className: cn(wrapperStyles, "flex h-min flex-wrap items-center justify-center gap-2 px-4 sm:justify-between")
      }
    ],
    [
      false,
      {
        justify: "space-between",
        align: "center",
        pos: "sticky",
        top: "6.25rem",
        className: cn(wrapperStyles)
      }
    ]
  ]);

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <Stack gap="xl" pos="relative" mt={isMobile ? `${wrapperRect?.height || 0}px` : undefined}>
        <Wrapper {...WrapperProps.get(isMobile)} ref={wrapperRef}>
          <Title order={2}>Configurações do Sistema</Title>

          <div className="flex flex-wrap items-center gap-1">
            <DashboardComponents.RefreshButton
              onClick={handleRefetch}
              loading={isFetching || isFetchingIp}
              size="input-sm"
              radius="sm"
              tooltipProps={{ label: "Atualizar Configurações" }}
            />

            <Tooltip label="Nenhuma alteração identificada" disabled={isDirty}>
              <Button type="submit" variant="filled" color="blue" loading={isSubmitting || isFetching} disabled={!isSuccess || !isDirty}>
                Salvar Configurações
              </Button>
            </Tooltip>
          </div>
        </Wrapper>

        {/* Controles de Fila */}
        <Card component={Stack} shadow="sm" p="lg" radius="md" withBorder>
          <Group justify="space-between" align="center">
            <Title order={4}>Controles de Fila</Title>

            <Button
              rightSection={<WarningCircleIcon weight="fill" size={20} />}
              size="compact-lg"
              color="red"
              onClick={handleResetQueue}
              loading={isLoading}
              disabled={disabled}
              fullWidth={isMobile}
            >
              Resetar fila
            </Button>
          </Group>

          <Group justify="space-between">
            <Box>
              <Text size="sm" fw={500}>
                Fila Fechada/Fila Aberta
              </Text>
              <Text size="xs" c="dimmed">
                Controla se a fila está ativa ou fechada
              </Text>
            </Box>

            <Input.Switch name="enableQueue" control={control} onLabel="Aberta" offLabel="Fechada" />
          </Group>

          <Group justify="space-between">
            <Box>
              <Text size="sm" fw={500}>
                Motorista Autônomo
              </Text>
              <Text size="xs" c="dimmed">
                Habilitar/desabilitar motoristas autônomos
              </Text>
            </Box>

            <Input.Switch name="enableQueueWithoutScheduling" control={control} onLabel="Habilitado" offLabel="Desabilitado" />
          </Group>
        </Card>

        {/* Check-In IPs */}
        <Card component={Stack} shadow="sm" p="lg" radius="md" withBorder>
          <Stack gap="xs">
            <div className="flex flex-wrap items-center justify-center gap-[inherit] md:justify-between">
              <Title order={4}>IPs de Check-In Motoristas</Title>

              {!isFetchingIp && isSuccessIp && (
                <Tooltip label="Clique para copiar o seu IP" disabled={clipboard.copied}>
                  <Badge
                    color={clipboard.copied ? "green" : "yellow"}
                    size="lg"
                    onClick={() => clipboard.copy(ipData?.ip)}
                    leftSection={
                      clipboard.copied && (
                        <ActionIcon size="xs" color="white" variant="transparent">
                          <CheckIcon />
                        </ActionIcon>
                      )
                    }
                  >
                    {ipData?.ip}
                  </Badge>
                </Tooltip>
              )}

              {isFetchingIp && <Loader size="sm" />}
            </div>

            <Text c="dimmed" size="sm" className="max-md:text-center">
              Você pode consultar por IPs por meio{" "}
              <Anchor href="https://meuip.com.br" referrerPolicy="no-referrer" target="_blank">
                desse link
              </Anchor>
            </Text>
          </Stack>

          <div className="grid grid-cols-1 gap-[inherit] md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
            {fields.map((field, index) => {
              const disabled = index <= 1;
              const indexAsOne = index + 1;

              return (
                <Input.Text
                  key={field.id}
                  name={`checkinIp.${index}.value`}
                  control={control}
                  label={`IP Check-In Motoristas ${indexAsOne}`}
                  placeholder={`Endereço IP - Ex: 192.168.0.${indexAsOne}`}
                  rightSection={
                    <Tooltip label="Deletar IP" color="red" disabled={disabled}>
                      <ActionIcon variant="light" color="red" onClick={() => remove(index)} disabled={disabled} radius="sm">
                        <TrashIcon />
                      </ActionIcon>
                    </Tooltip>
                  }
                />
              );
            })}

            <div className="place-self-center self-end">
              <Tooltip label="Novo IP" color="blue" disabled={disabled}>
                <ActionIcon variant="light" color="blue" onClick={() => append({ value: "" })} disabled={disabled} radius="sm">
                  <PlusIcon />
                </ActionIcon>
              </Tooltip>
            </div>
          </div>

          <Group justify="space-between">
            <Box>
              <Text size="sm" fw={500}>
                Check-In fechada/aberta
              </Text>
              <Text size="xs" c="dimmed">
                Controla se o check-in está disponível
              </Text>
            </Box>

            <Input.Switch name="enableCheckin" control={control} onLabel="Aberto" offLabel="Fechado" />
          </Group>
        </Card>

        {/* Informações dos Atendimentos */}
        <Card component={Stack} shadow="sm" p="lg" radius="md" withBorder>
          <Title order={4}>Informações dos Atendimentos</Title>

          <div className="grid grid-cols-1 gap-[inherit] md:grid-cols-2">
            <Input.Text name="startTime" control={control} label="Horário de Início" type="time" />
            <Input.Text name="endTime" control={control} label="Horário de Término" type="time" />

            <Input.Text name="intervalStartTime" control={control} label="Intervalo - Início" type="time" />
            <Input.Text name="intervalEndTime" control={control} label="Intervalo - Término" type="time" />

            <Input.Number name="hour_entry_limit_attended" control={control} label="Nº máximo de horas para entrada de atendidos:" min={0} max={24} />
            <Input.Number name="hour_entry_limit_missing" control={control} label="Nº máximo de horas para entrada de faltosos:" min={0} max={24} />

            <Tooltip label="Campo desabilitado, por padrão não haverá limite máximo de atendimentos por dia!" color="red">
              <Input.Number name="maxDailyServices" control={control} label="Nº máximo de atendimentos por dia" min={0} readOnly disabled />
            </Tooltip>

            <Input.Text name="serviceTime" control={control} label="Tempo por atendimento" type="time" />
          </div>

          <Input.Number name="editHoursLimit" control={control} label="Limite de horas para edição de carregamentos" min={0} max={24} />

          <Box>
            <Text size="sm" fw={500} mb="xs">
              Dias de atendimento:
            </Text>
            <div className="flex flex-col flex-wrap gap-2 sm:flex-row">
              <Input.Switch name="weekDays.sun" control={control} label="Dom" size="md" />
              <Input.Switch name="weekDays.mon" control={control} label="Seg" size="md" />
              <Input.Switch name="weekDays.tue" control={control} label="Ter" size="md" />
              <Input.Switch name="weekDays.wed" control={control} label="Qua" size="md" />
              <Input.Switch name="weekDays.thu" control={control} label="Qui" size="md" />
              <Input.Switch name="weekDays.fri" control={control} label="Sex" size="md" />
              <Input.Switch name="weekDays.sat" control={control} label="Sáb" size="md" />
            </div>
          </Box>

          <Group align="end" display="flex" wrap="wrap">
            <Input.Text name="delayTolerance" control={control} label="Tolerância de Atraso" type="time" className="flex-[1_1_min(100%,175px)]" />
            <Input.Switch name="enableDelayTolerance" control={control} label="Habilitar tolerância" />
          </Group>

          <Group align="end" display="flex" wrap="wrap">
            <Input.Text name="recaptchaSecret" control={control} label="Chave Recaptcha Backend" placeholder="Chave" className="flex-[1_1_min(100%,175px)]" />
            <Input.Switch name="enableRecaptcha" control={control} label="Habilitar Recaptcha" />
          </Group>
        </Card>

        {/* Informações dos Pedidos */}
        <Card component={Stack} shadow="sm" p="lg" radius="md" withBorder>
          <Title order={4}>Informações dos Pedidos</Title>

          <div className="grid grid-cols-1 gap-[inherit] md:grid-cols-2">
            <Input.Number name="schedulingDayLimit" control={control} label="Limite de agendamento por pedido" min={0} />
            <Input.Number name="hourEntryLimit" control={control} label="Intervalo entre último agendamento (Horas)" min={0} />
          </div>
        </Card>

        {/* Informações de Bloqueio */}
        <Card component={Stack} shadow="sm" p="lg" radius="md" withBorder>
          <Title order={4}>Informações de Bloqueio</Title>

          <Group align="end" display="flex" wrap="wrap">
            <Input.Number name="missingLimit" control={control} label="Limite de faltas" min={0} className="flex-[1_1_min(100%,175px)]" />
            <Input.Switch name="enableMissingValidation" control={control} label="Habilitar Limite de Falta" />
          </Group>

          <div className="grid grid-cols-1 gap-[inherit] md:grid-cols-2">
            <Input.Number name="missingValidationPeriod" control={control} label="Período de Validação (Dias)" min={0} />
            <Input.Number name="missingLimitAfterBlock" control={control} label="Limite de faltas após bloqueio" min={0} />
          </div>
        </Card>
      </Stack>
    </form>
  );
}
